import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth } from "@clerk/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ReactCountryFlag from "react-country-flag";
import { CheckCircle2 } from "lucide-react";
import type { AthleteProfile } from "@/components/athlete/types";
import { authFetch } from "@/lib/api";

export function AthleteHeader({
  athlete,
  lastAgeGroup,
  isClaimed,
  isOwnProfile,
}: {
  athlete: AthleteProfile;
  lastAgeGroup?: string | null;
  isClaimed?: boolean;
  isOwnProfile?: boolean;
}) {
  const { isSignedIn, getToken } = useAuth();
  const queryClient = useQueryClient();
  const [claimError, setClaimError] = useState<string | null>(null);

  const claimMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return authFetch("/users/claim-athlete", {
        method: "POST",
        body: { athleteId: athlete.id },
        token,
      });
    },
    onSuccess: () => {
      setClaimError(null);
      queryClient.invalidateQueries({ queryKey: ["athlete", athlete.id] });
      queryClient.invalidateQueries({ queryKey: ["user-profile"] });
    },
    onError: (err: Error) => {
      setClaimError(err.message);
    },
  });

  const location =
    [athlete.city, athlete.state, athlete.country]
      .filter(Boolean)
      .join(", ") || "Location unavailable";

  const repIso2 =
    athlete.representingCountryIso2 ?? athlete.countryIso2 ?? null;
  const repLabel =
    athlete.representingCountryName ?? athlete.country ?? null;

  return (
    <div className="athlete-hero d8-reveal d8-reveal-delay-1 revealed">
      <p className="athlete-eyebrow">
        Athlete Profile
        {isOwnProfile && (
          <span className="athlete-claimed-badge">
            <CheckCircle2 size={14} />
            Claimed
          </span>
        )}
      </p>
      <h1 className="athlete-name">{athlete.fullName}</h1>
      <div className="athlete-meta">
        <span>{location}</span>
        {repIso2 && (
          <span className="athlete-pill athlete-pill--flag">
            <ReactCountryFlag
              countryCode={repIso2}
              svg
              style={{
                width: "1em",
                height: "1em",
                marginRight: "0.35em",
                verticalAlign: "-0.1em",
              }}
              title={repLabel ?? repIso2}
            />
            {repLabel ?? repIso2}
          </span>
        )}
        {lastAgeGroup && (
          <span className="athlete-pill">{lastAgeGroup}</span>
        )}
        {athlete.gender && (
          <span className="athlete-pill">{athlete.gender}</span>
        )}
        <Link
          to="/compare"
          search={{ a: athlete.id }}
          className="athlete-pill athlete-pill--compare"
        >
          Compare
        </Link>
        {isSignedIn && !isClaimed && !isOwnProfile && (
          <button
            type="button"
            className="athlete-pill athlete-pill--claim"
            onClick={() => claimMutation.mutate()}
            disabled={claimMutation.isPending}
          >
            {claimMutation.isPending ? "Claiming..." : "This is me"}
          </button>
        )}
      </div>
      {claimError && (
        <p className="athlete-claim-error">{claimError}</p>
      )}
    </div>
  );
}
